import { useMemo } from "react";
import { bunader } from "@/data/bunader";
import { tilbehorCategories } from "@/data/tilbehor";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface SearchResult {
  id: string;
  type: "bunad" | "tilbehor" | "faq" | "blogg" | "side";
  title: string;
  description: string;
  image?: string;
  url: string;
  relevance: number;
}

// Static FAQ data for search
const faqItems = [
  {
    id: "faq-1",
    question: "Hvor lang tid tar det å sy en bunad?",
    answer: "Det tar vanligvis 6-12 måneder å sy en komplett bunad, avhengig av kompleksiteten og vår arbeidsmengde."
  },
  {
    id: "faq-2",
    question: "Kan jeg bestille bunad til barn?",
    answer: "Ja, vi syr bunader til både barn og voksne. Barnebunader kan tilpasses for å kunne sys ut etter hvert som barnet vokser."
  },
  {
    id: "faq-3",
    question: "Hvordan tar jeg vare på bunaden min?",
    answer: "Bunaden bør oppbevares hengende i et bunadsbeskyttelse eller poseskap. Unngå å oppbevare den i plast."
  },
  {
    id: "faq-4",
    question: "Tilbyr dere tilpasninger av eksisterende bunader?",
    answer: "Ja, vi kan gjøre tilpasninger på eksisterende bunader, inkludert endring av størrelse, reparasjoner og oppfriskning av broderier."
  },
  {
    id: "faq-5",
    question: "Kan jeg komme til prøvetime?",
    answer: "Absolutt! Vi anbefaler prøvetimer for å sikre perfekt passform. Ta kontakt for å avtale en time."
  },
  {
    id: "faq-6",
    question: "Hvilke betalingsalternativer har dere?",
    answer: "Vi tilbyr flere betalingsalternativer, inkludert delbetaling. Ta kontakt med oss for mer informasjon."
  },
  {
    id: "faq-7",
    question: "Selger dere brukte bunader?",
    answer: "Vi formidler brukte bunader fra tid til annen. Ta kontakt for å høre om vi har noe som passer for deg."
  },
  {
    id: "faq-8",
    question: "Hvor kommer materialene fra?",
    answer: "Vi bruker ullstoff fra Lillehammer, garn fra Rauma, og broderier utføres av dyktige håndverkere over hele Norge."
  }
];

// Static pages for search
const staticPages = [
  {
    id: "historie",
    title: "Vår historie",
    description: "Røros Husflidutsalg ble etablert av Iver Tønseth i 1934. Les om Bunadstuggu sin historie fra Røros til Levanger.",
    url: "/#historie"
  },
  {
    id: "kontakt",
    title: "Kontakt oss",
    description: "Besøk oss i Kirkegata 38, 7600 Levanger. Telefon: 480 09 271. Vi hjelper deg med bunad og tilbehør.",
    url: "/#kontakt"
  }
];

function normalizeText(text: string): string {
  return text.toLowerCase()
    .replace(/[æ]/g, "ae")
    .replace(/[ø]/g, "o")
    .replace(/[å]/g, "a")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}

function calculateRelevance(searchTerms: string[], textFields: string[]): number {
  let score = 0;
  const normalizedFields = textFields.map(f => normalizeText(f));
  
  for (const term of searchTerms) {
    const normalizedTerm = normalizeText(term);
    
    for (let i = 0; i < normalizedFields.length; i++) {
      const field = normalizedFields[i];
      // Exact match in title gets highest score
      if (i === 0 && field.includes(normalizedTerm)) {
        score += 100;
        // Bonus for exact word match
        if (field.split(/\s+/).includes(normalizedTerm)) {
          score += 50;
        }
      } else if (field.includes(normalizedTerm)) {
        score += 20;
      }
    }
  }
  
  return score;
}

export function useSearch(query: string) {
  // Fetch blog posts from Supabase
  const { data: blogPosts = [] } = useQuery({
    queryKey: ["blog-posts-search"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("blog_posts")
        .select("id, title, excerpt, image_url, published")
        .eq("published", true);
      
      if (error) throw error;
      return data || [];
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  const results = useMemo(() => {
    if (!query.trim()) return [];

    const searchTerms = query.toLowerCase().trim().split(/\s+/).filter(t => t.length > 1);
    if (searchTerms.length === 0) return [];

    const allResults: SearchResult[] = [];

    // Search bunader
    for (const bunad of bunader) {
      const textFields = [
        bunad.title,
        bunad.shortDescription,
        ...bunad.fullDescription,
        ...bunad.features,
        ...bunad.colors
      ];
      
      const relevance = calculateRelevance(searchTerms, textFields);
      
      if (relevance > 0) {
        allResults.push({
          id: bunad.id,
          type: "bunad",
          title: bunad.title,
          description: bunad.shortDescription,
          image: bunad.images[0],
          url: `/drakt/${bunad.id}`,
          relevance
        });
      }
    }

    // Search tilbehør
    for (const category of tilbehorCategories) {
      const textFields = [
        category.title,
        category.modalTitle,
        category.description
      ];
      
      const relevance = calculateRelevance(searchTerms, textFields);
      
      if (relevance > 0) {
        allResults.push({
          id: category.id,
          type: "tilbehor",
          title: category.title,
          description: category.description,
          image: category.images[0],
          url: `/tilbehor#${category.id}`,
          relevance
        });
      }
    }

    // Search FAQ
    for (const faq of faqItems) {
      const textFields = [faq.question, faq.answer];
      const relevance = calculateRelevance(searchTerms, textFields);
      
      if (relevance > 0) {
        allResults.push({
          id: faq.id,
          type: "faq",
          title: faq.question,
          description: faq.answer.substring(0, 120) + "...",
          url: "/sporsmal",
          relevance
        });
      }
    }

    // Search blog posts
    for (const post of blogPosts) {
      const textFields = [post.title, post.excerpt || ""];
      const relevance = calculateRelevance(searchTerms, textFields);
      
      if (relevance > 0) {
        allResults.push({
          id: post.id,
          type: "blogg",
          title: post.title,
          description: post.excerpt || "",
          image: post.image_url || undefined,
          url: `/blogg/${post.id}`,
          relevance
        });
      }
    }

    // Search static pages
    for (const page of staticPages) {
      const textFields = [page.title, page.description];
      const relevance = calculateRelevance(searchTerms, textFields);
      
      if (relevance > 0) {
        allResults.push({
          id: page.id,
          type: "side",
          title: page.title,
          description: page.description,
          url: page.url,
          relevance
        });
      }
    }

    // Sort by relevance (highest first)
    return allResults.sort((a, b) => b.relevance - a.relevance);
  }, [query, blogPosts]);

  return results;
}

// Get type label in Norwegian
export function getTypeLabel(type: SearchResult["type"]): string {
  const labels: Record<SearchResult["type"], string> = {
    bunad: "Bunad",
    tilbehor: "Tilbehør",
    faq: "Spørsmål",
    blogg: "Artikkel",
    side: "Side"
  };
  return labels[type];
}
